import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { type NextStep, springConfig } from './constants'

interface NextStepButtonProps {
  step: NextStep
  isLoading?: boolean
  onClick: () => void
}

export function NextStepButton({ step, isLoading, onClick }: NextStepButtonProps) {
  const StepIcon = step.icon
  const isPrimary = step.priority === 'primary'

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springConfig.default}
      whileHover={{ y: -2, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      disabled={isLoading}
      className={`
        relative flex flex-col items-center justify-center
        p-4 rounded-xl border-2 transition-all duration-200
        min-h-[100px] w-full
        ${isPrimary
          ? 'bg-gradient-to-br from-blue-50 to-white border-blue-200 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/10'
          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
        }
        ${isLoading ? 'opacity-70 cursor-wait' : 'cursor-pointer'}
        disabled:opacity-50 disabled:cursor-not-allowed
        group
      `}
    >
      {/* Icon container */}
      <div
        className={`
          w-10 h-10 rounded-lg flex items-center justify-center mb-2
          transition-colors duration-200
          ${isPrimary
            ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-200'
            : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
          }
        `}
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <StepIcon className="w-5 h-5" />
        )}
      </div>

      {/* Label */}
      <span
        className={`
          text-sm font-medium text-center leading-tight
          ${isPrimary ? 'text-slate-900' : 'text-slate-700'}
        `}
      >
        {step.label}
      </span>

      {/* Description (optional) */}
      {step.description && (
        <span className="text-xs text-slate-500 text-center mt-0.5 line-clamp-1">
          {step.description}
        </span>
      )}

      {/* Primary indicator ring on hover */}
      {isPrimary && (
        <motion.div
          className="absolute inset-0 rounded-xl border-2 border-blue-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          layoutId="primary-ring"
        />
      )}
    </motion.button>
  )
}
